import { viewKey } from './follow.js';
import type { FollowView } from './follow.js';

/**
 * WHETHER A ROOM'S MOVE REACHES THIS READER — §11's rules, as decisions rather than as effects.
 *
 * `follow.ts` is the MESSAGE: what a view is, how it goes on the wire, how it comes back. This is
 * what a surface does with one, and it is here rather than in the panel that currently asks because
 * the phone will ask the same four questions and must not answer them differently. Moving three
 * hundred readers to another verse is a griefing vector before it is a feature, so "who may drive"
 * is the last rule in this layer that should exist in two copies — `licence-policy.ts` is the scar
 * this repository keeps about exactly that, where two files stated one rule and disagreed.
 *
 * NOTHING HERE TOUCHES A TRANSPORT AND NOTHING HERE HOLDS STATE. Every function takes what the
 * caller already has — the roster it is drawing, the view it is showing — and returns a decision.
 * The bookkeeping stays with the surface, because a ref is a React fact and a phone may keep the
 * same two strings somewhere else entirely.
 */

/**
 * One row of the roster, as far as following is concerned.
 *
 * `canSpeak` is the capability the SERVER minted (§5.2 `capabilitiesFor`), which is exactly §11's
 * rule — hosts and speakers — arriving by the path that already keeps the two ends from disagreeing.
 * A surface passes the rows it is already drawing; this asks for no lookup of its own.
 */
export interface FollowDriver {
  identity: string;
  canSpeak: boolean;
}

/**
 * IS THIS VIEW ABOUT THE ROOM AT ALL — one rule, asked from both ends.
 *
 * Everybody in a room is on the room's node, because the panel that draws the room is that node's.
 * So a driver who has walked to another passage has LEFT, and the roster says so a moment later.
 *
 * THE SENDING END uses this to refuse to publish such a view: there is no move to send, and a
 * sentence about a room that "moved" would describe something that did not happen. That answer was
 * measured rather than reasoned — a live gate asserted the opposite first and went red, because
 * walking away unmounts the panel, which disconnects, and the publish loses the race with its own
 * transport closing.
 *
 * THE RECEIVING END uses it as a defence: the only way such a packet arrives is one that should not
 * exist, and without this check a forged move would drag every follower out of the room.
 *
 * Both ends were asking it separately, in two places, with two different comments. One rule.
 */
export function aboutThisRoom(view: FollowView | undefined, roomNodeId: string): boolean {
  return (view?.node ?? null) === roomNodeId;
}

/**
 * MAY THIS SENDER MOVE OTHER PEOPLE — asked of the roster, never of the packet.
 *
 * A packet cannot carry authority: anybody in the room can publish one, and `from` is what the SFU
 * knows rather than what the sender claims. A sender who is not on the roster at all is not refused
 * as a stranger — they are somebody this surface has not been told about yet, and applying their
 * move would be trusting the packet.
 */
export function mayDrive(roster: readonly FollowDriver[], identity: string): boolean {
  return roster.find((one) => one.identity === identity)?.canSpeak === true;
}

/** An arriving move, with everything the decision needs and nothing it does not. */
export interface ArrivingMove {
  /** Whether this reader is still following. Opt-out is per reader and §11 is explicit about it. */
  following: boolean;
  /** The roster this surface is already drawing. */
  roster: readonly FollowDriver[];
  /** Who the SFU says sent it. Never what the packet says. */
  from: string;
  /** Where they say the room is looking. */
  moved: FollowView;
  /** The node whose panel this is. */
  roomNodeId: string;
}

/**
 * Whether an arriving move should move this reader.
 *
 * Every refusal here is silent and deliberately so: no reader acted, and nothing of theirs is lost
 * by a packet that goes nowhere. A line about a move that was dropped would be a line about
 * somebody else's misbehaviour, in front of a room, with nothing anyone can do about it.
 */
export function movesTheReader(arriving: ArrivingMove): boolean {
  if (!arriving.following) return false;
  if (!mayDrive(arriving.roster, arriving.from)) return false;
  return aboutThisRoom(arriving.moved, arriving.roomNodeId);
}

/**
 * Who moved the view this surface is showing.
 *
 * It changes for two quite different reasons and telling them apart is what makes both halves of
 * §11 work: a leader's move goes out, and a FOLLOWER who navigates away stops following without
 * pressing anything — the piece §11 asks for that the control alone cannot give.
 *
 *   `nobody`      the key did not change. A leader pointing where this reader already is changes
 *                 nothing, and a surface that recorded it anyway would swallow the reader's own
 *                 next move.
 *   `the room`    a move this surface applied has landed — or the app landed near it, when a part
 *                 did not resolve. Either way it is not the reader's doing.
 *   `this reader` they moved. What that MEANS is the caller's to decide, because it divides on a
 *                 capability this file deliberately knows nothing about: a driver publishes where
 *                 they are, and a follower has just stopped following.
 */
export type WhoMoved = 'nobody' | 'the room' | 'this reader';

/**
 * @param now     the key of the view now showing
 * @param seen    the key this surface last accounted for
 * @param applied the key of a move it handed up and is still waiting to see land, or null
 */
export function whoMovedTheView(now: string, seen: string, applied: string | null): WhoMoved {
  if (now === seen) return 'nobody';
  return applied === null ? 'this reader' : 'the room';
}

/**
 * Whether a move is worth recording as pending — it is not, when it would change nothing.
 *
 * Paired with `whoMovedTheView`, and separate from it because the two are asked at different
 * moments: this when a move arrives, that when the view actually changes. A move to where the
 * reader already is never produces a change to attribute, so recording it would leave a surface
 * waiting for a landing that cannot come, and the reader's next move would be read as the room's.
 *
 * The keys come from `viewKey`, so a view the app built and one off the wire compare equal whatever
 * order each was assembled in.
 */
export function willChangeTheView(moved: FollowView, seen: string): boolean {
  return viewKey(moved) !== seen;
}
